import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, Form, Row, Col, Table, Button, Spinner, Alert, Modal, InputGroup } from 'react-bootstrap';
import PageHeader from '../components/PageHeader';
import StatusBadge from '../components/StatusBadge';
import { PartyBadgeList } from '../components/PartyBadge';
import Pagination from '../components/Pagination';
import { adminApi, extractError } from '../api/client';
import { STATUS_OPTIONS, fmtRelative } from '../lib/lookups';
import { useStates, useCounties } from '../hooks/useLocations';
import { useToast } from '../contexts/ToastContext';

export default function CandidatesListPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [q, setQ] = useState('');
  const [homeStateId, setHomeStateId] = useState('');
  const [homeCountyId, setHomeCountyId] = useState('');
  const [status, setStatus] = useState('');

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteForce, setDeleteForce] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Bulk selection / actions
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkConfirm, setBulkConfirm] = useState(null);    // {target, ids, scope}
  const [bulkResult, setBulkResult] = useState(null);      // server response after a run

  const { states } = useStates();
  const { counties: filterCounties } = useCounties(homeStateId);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total, pageSize]);

  useEffect(() => {
    let active = true;
    async function load() {
      setLoading(true);
      setError('');
      try {
        const params = { page, page_size: pageSize };
        if (q.trim()) params.q = q.trim();
        if (homeStateId) params.home_state_id = homeStateId;
        if (homeCountyId) params.home_county_id = homeCountyId;
        if (status) params.status = status;
        const res = await adminApi.get('/admin/candidates', { params });
        if (!active) return;
        setItems(res.data.items || []);
        setTotal(res.data.total || 0);
      } catch (e) {
        if (active) setError(extractError(e));
      } finally {
        if (active) setLoading(false);
      }
    }
    const t = setTimeout(load, 200);
    return () => { active = false; clearTimeout(t); };
  }, [page, pageSize, q, homeStateId, homeCountyId, status]);

  function clearSelection() { setSelectedIds(new Set()); }

  function toggleSelected(id) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function selectAllInPage() {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const c of items) next.add(c.id);
      return next;
    });
  }

  const allOnPageSelected = items.length > 0 && items.every((c) => selectedIds.has(c.id));

  function openBulkConfirm({ target, scope }) {
    setBulkResult(null);
    if (scope === 'selected') {
      setBulkConfirm({ target, scope, ids: Array.from(selectedIds) });
    } else {
      // 'filtered' — we don't pass ids; we'll fetch the full filtered set
      // server-side when confirming, capped by `total`.
      setBulkConfirm({ target, scope, ids: null });
    }
  }

  async function runBulk() {
    if (!bulkConfirm) return;
    let ids = bulkConfirm.ids;
    setBulkRunning(true);
    try {
      // For 'filtered' scope, fetch all matching ids first (the candidates
      // endpoint exposes a page_size cap of 100 — but we can iterate).
      if (!ids) {
        const collected = [];
        const params = {
          page: 1, page_size: 100,
          ...(q.trim() ? { q: q.trim() } : {}),
          ...(homeStateId ? { home_state_id: homeStateId } : {}),
          ...(homeCountyId ? { home_county_id: homeCountyId } : {}),
          ...(status ? { status } : {}),
        };
        let pageN = 1;
        while (collected.length < total) {
          const res = await adminApi.get('/admin/candidates', { params: { ...params, page: pageN } });
          const batch = res.data.items || [];
          if (!batch.length) break;
          for (const c of batch) collected.push(c.id);
          if (batch.length < params.page_size) break;
          pageN += 1;
          if (pageN > 50) break;   // safety cap = 5,000 candidates per bulk run
        }
        ids = collected;
      }
      if (!ids.length) {
        toast.error('No candidates to process');
        return;
      }
      const res = await adminApi.post('/admin/candidates/bulk-transition', {
        candidate_ids: ids,
        target_status: bulkConfirm.target,
      });
      const r = res.data || {};
      setBulkResult(r);
      const summary = `${r.succeeded || 0} ${bulkConfirm.target}` +
        (r.skipped ? `, ${r.skipped} already at target` : '') +
        (r.failed ? `, ${r.failed} failed` : '');
      if (r.failed) toast.error(`Bulk done with errors — ${summary}`);
      else toast.success(`Bulk transition complete — ${summary}`);
      // Refresh the list so updated statuses appear.
      setPage(1);
      // Re-trigger the load by tweaking page (no-op) — the effect will re-run
      // when selection clears and we set page = 1.
      clearSelection();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setBulkRunning(false);
      setBulkConfirm(null);
    }
  }

  function clearFilters() {
    clearSelection();
    setQ(''); setHomeStateId(''); setHomeCountyId(''); setStatus(''); setPage(1);
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const url = `/admin/candidates/${deleteTarget.id}${deleteForce ? '?force=true' : ''}`;
      await adminApi.delete(url);
      toast.success(`${deleteTarget.full_name} deleted`);
      setDeleteTarget(null);
      setDeleteForce(false);
      if (items.length === 1 && page > 1) setPage(page - 1);
      setItems((arr) => arr.filter((x) => x.id !== deleteTarget.id));
      setTotal((t) => t - 1);
    } catch (e) {
      const msg = extractError(e);
      if (e?.response?.status === 409) {
        // Surface the race-link block so admin can re-confirm with force
        toast.error(msg);
        setDeleteForce(true);
      } else {
        toast.error(msg);
      }
    } finally {
      setDeleting(false);
    }
  }

  const stateName = (id) => (states || []).find((s) => s.id === id)?.name;

  const activeFilters = [
    homeStateId && { key: 'home_state_id', label: `Home state: ${stateName(homeStateId) || homeStateId}`, clear: () => { setHomeStateId(''); setHomeCountyId(''); } },
    homeCountyId && { key: 'home_county_id', label: 'Home county set', clear: () => setHomeCountyId('') },
    status && { key: 'status', label: `Status: ${STATUS_OPTIONS.find((s) => s.value === status)?.label || status}`, clear: () => setStatus('') },
    q && { key: 'q', label: `Search: "${q}"`, clear: () => setQ('') },
  ].filter(Boolean);

  return (
    <>
      <PageHeader
        title="Candidates"
        subtitle={`${total} total — candidate profiles. Link them to races on each Election Day's detail page.`}
        actions={
          <Button variant="primary" onClick={() => navigate('/candidates/create')} data-testid="candidates-create-button">
            <i className="bi bi-plus-lg me-1" /> New candidate
          </Button>
        }
      />
      <div className="page-content">
        <Card className="mb-3">
          <Card.Body>
            <Row className="g-2 align-items-end">
              <Col xs={12} md={5}>
                <Form.Label className="mb-1" style={{ fontSize: '0.82rem' }}>Search</Form.Label>
                <InputGroup>
                  <InputGroup.Text><i className="bi bi-search" /></InputGroup.Text>
                  <Form.Control
                    placeholder="Name or slug…"
                    value={q}
                    onChange={(e) => { clearSelection(); setPage(1); setQ(e.target.value); }}
                    data-testid="candidates-search-input"
                  />
                </InputGroup>
              </Col>
              <Col xs={6} md={3}>
                <Form.Label className="mb-1" style={{ fontSize: '0.82rem' }}>Home state</Form.Label>
                <Form.Select value={homeStateId} onChange={(e) => { clearSelection(); setPage(1); setHomeStateId(e.target.value); setHomeCountyId(''); }} data-testid="candidates-filter-state-select">
                  <option value="">All</option>
                  {(states || []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </Form.Select>
              </Col>
              <Col xs={6} md={2}>
                <Form.Label className="mb-1" style={{ fontSize: '0.82rem' }}>Home county</Form.Label>
                <Form.Select
                  value={homeCountyId}
                  onChange={(e) => { clearSelection(); setPage(1); setHomeCountyId(e.target.value); }}
                  disabled={!homeStateId || filterCounties.length === 0}
                  data-testid="candidates-filter-county-select"
                >
                  <option value="">
                    {!homeStateId ? 'Pick state first' : filterCounties.length === 0 ? 'No counties' : 'All'}
                  </option>
                  {filterCounties.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs={6} md={2}>
                <Form.Label className="mb-1" style={{ fontSize: '0.82rem' }}>Status</Form.Label>
                <Form.Select value={status} onChange={(e) => { clearSelection(); setPage(1); setStatus(e.target.value); }} data-testid="candidates-filter-status-select">
                  <option value="">All</option>
                  {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </Form.Select>
              </Col>
            </Row>
            {activeFilters.length > 0 && (
              <div className="d-flex gap-2 flex-wrap mt-3 align-items-center">
                {activeFilters.map((f) => (
                  <span key={f.key} className="filter-chip" data-testid="active-filter-chip">
                    {f.label}
                    <button onClick={f.clear} aria-label={`Clear ${f.key}`}><i className="bi bi-x" /></button>
                  </span>
                ))}
                <Button variant="link" size="sm" onClick={clearFilters} data-testid="candidates-clear-filters-button">
                  Clear all
                </Button>
              </div>
            )}
          </Card.Body>
        </Card>

        {error && <Alert variant="danger">{error}</Alert>}

        {/* Bulk-action toolbar — appears whenever there's a selection OR a filter is active. */}
        {(selectedIds.size > 0 || activeFilters.length > 0) && !loading && items.length > 0 && (
          <Card className="mb-3 border-primary-subtle" data-testid="candidates-bulk-toolbar">
            <Card.Body className="d-flex flex-wrap align-items-center gap-2 py-2">
              <div className="me-auto small">
                {selectedIds.size > 0 ? (
                  <>
                    <strong>{selectedIds.size}</strong> selected
                    <Button variant="link" size="sm" className="p-0 ms-2" onClick={clearSelection} data-testid="candidates-bulk-clear-selection">
                      Clear
                    </Button>
                  </>
                ) : (
                  <span className="text-muted">No rows selected — use the checkboxes, or use “…matching filter” to act on the entire filtered set.</span>
                )}
              </div>
              {selectedIds.size > 0 && (
                <>
                  <Button size="sm" variant="success"
                    onClick={() => openBulkConfirm({ target: 'published', scope: 'selected' })}
                    disabled={bulkRunning}
                    data-testid="candidates-bulk-publish-selected">
                    <i className="bi bi-broadcast me-1" /> Publish {selectedIds.size}
                  </Button>
                  <Button size="sm" variant="outline-primary"
                    onClick={() => openBulkConfirm({ target: 'reviewed', scope: 'selected' })}
                    disabled={bulkRunning}
                    data-testid="candidates-bulk-review-selected">
                    Mark reviewed
                  </Button>
                  <Button size="sm" variant="outline-secondary"
                    onClick={() => openBulkConfirm({ target: 'archived', scope: 'selected' })}
                    disabled={bulkRunning}
                    data-testid="candidates-bulk-archive-selected">
                    Archive
                  </Button>
                </>
              )}
              {activeFilters.length > 0 && total > 0 && (
                <>
                  <span className="text-muted small">|</span>
                  <Button size="sm" variant="outline-success"
                    onClick={() => openBulkConfirm({ target: 'published', scope: 'filtered' })}
                    disabled={bulkRunning}
                    data-testid="candidates-bulk-publish-filtered">
                    <i className="bi bi-broadcast me-1" /> Publish ALL {total} matching filter
                  </Button>
                </>
              )}
            </Card.Body>
          </Card>
        )}

        <Card>
          {loading ? (
            <div className="text-center p-5"><Spinner animation="border" /></div>
          ) : items.length === 0 ? (
            <div className="empty-state">
              <i className="bi bi-inbox" />
              <div>No candidates match your filters.</div>
              <Button variant="link" onClick={clearFilters}>Clear filters</Button>
            </div>
          ) : (
            <>
              <Table hover responsive size="sm" className="mb-0" data-testid="candidates-table">
                <thead>
                  <tr>
                    <th style={{ width: 36 }}>
                      <Form.Check
                        type="checkbox"
                        checked={allOnPageSelected}
                        onChange={() => allOnPageSelected ? clearSelection() : selectAllInPage()}
                        aria-label="Select all on this page"
                        data-testid="candidates-select-all-page-checkbox"
                      />
                    </th>
                    <th>Name</th>
                    <th>Party</th>
                    <th>Home</th>
                    <th>Status</th>
                    <th>Updated</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((c) => (
                    <tr key={c.id} data-testid={`candidate-row-${c.id}`} className={selectedIds.has(c.id) ? 'table-active' : ''}>
                      <td>
                        <Form.Check
                          type="checkbox"
                          checked={selectedIds.has(c.id)}
                          onChange={() => toggleSelected(c.id)}
                          aria-label={`Select ${c.full_name}`}
                          data-testid={`candidate-select-${c.id}`}
                        />
                      </td>
                      <td>
                        <Link to={`/candidates/${c.id}`} className="text-decoration-none fw-semibold d-inline-flex align-items-center gap-1" data-testid={`candidate-name-link-${c.id}`}>
                          {c.full_name}
                          {c.verified && (
                            <i
                              className="bi bi-patch-check-fill text-success"
                              title="Verified profile"
                              aria-label="Verified"
                              style={{ fontSize: '0.9rem' }}
                              data-testid={`candidate-verified-icon-${c.id}`}
                            />
                          )}
                        </Link>
                        <div className="text-muted" style={{ fontSize: '0.78rem' }}>{c.slug}</div>
                      </td>
                      <td data-testid={`candidate-party-${c.id}`}>
                        <PartyBadgeList parties={c.parties || []} />
                      </td>
                      <td>
                        <span className="text-muted" style={{ fontSize: '0.85rem' }}>
                          {stateName(c.home_state_id) || '—'}
                        </span>
                      </td>
                      <td><StatusBadge status={c.status} /></td>
                      <td className="text-muted">{fmtRelative(c.updated_at)}</td>
                      <td className="text-end">
                        <div className="d-flex gap-1 justify-content-end">
                          <Link to={`/candidates/${c.id}`}><Button size="sm" variant="outline-secondary" data-testid={`candidate-view-${c.id}`}>View</Button></Link>
                          <Link to={`/candidates/${c.id}/edit`}><Button size="sm" variant="outline-primary" data-testid={`candidate-edit-${c.id}`}>Edit</Button></Link>
                          <Button size="sm" variant="outline-danger" onClick={() => { setDeleteForce(false); setDeleteTarget(c); }} data-testid={`candidate-delete-${c.id}`}>Delete</Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <div className="d-flex justify-content-between align-items-center p-3 border-top">
                <div className="text-muted" style={{ fontSize: '0.85rem' }}>Page {page} of {totalPages} · {total} total</div>
                <Pagination page={page} totalPages={totalPages} onChange={(p) => { clearSelection(); setPage(p); }} />
                <Form.Select size="sm" value={pageSize} onChange={(e) => { clearSelection(); setPage(1); setPageSize(Number(e.target.value)); }} style={{ width: 100 }} data-testid="candidates-page-size-select">
                  {[10, 20, 50].map((n) => <option key={n} value={n}>{n}/page</option>)}
                </Form.Select>
              </div>
            </>
          )}
        </Card>

        <Modal show={!!deleteTarget} onHide={() => { setDeleteTarget(null); setDeleteForce(false); }} centered>
          <Modal.Header closeButton>
            <Modal.Title>Delete candidate</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>Delete <strong>{deleteTarget?.full_name}</strong>?</p>
            {deleteForce && (
              <Alert variant="warning" data-testid="candidate-delete-force-warning">
                This candidate is linked to one or more races. Confirming will also remove those race-candidate links.
              </Alert>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => { setDeleteTarget(null); setDeleteForce(false); }}>Cancel</Button>
            <Button variant="danger" onClick={confirmDelete} disabled={deleting} data-testid="candidate-delete-confirm-button">
              {deleting ? <Spinner size="sm" animation="border" /> : (deleteForce ? 'Delete & unlink races' : 'Delete')}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Bulk transition confirm */}
        <Modal show={!!bulkConfirm} onHide={() => !bulkRunning && setBulkConfirm(null)} centered data-testid="candidates-bulk-confirm-modal">
          <Modal.Header closeButton>
            <Modal.Title>Confirm bulk transition</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {bulkConfirm && (
              <>
                <p>
                  Set status to <strong>{bulkConfirm.target}</strong> for{' '}
                  {bulkConfirm.scope === 'selected'
                    ? <>the <strong>{bulkConfirm.ids?.length || 0}</strong> selected candidate{(bulkConfirm.ids?.length || 0) === 1 ? '' : 's'}</>
                    : <>all <strong>{total}</strong> candidate{total === 1 ? '' : 's'} matching the current filter</>}
                  ?
                </p>
                {bulkConfirm.target === 'published' && (
                  <Alert variant="info" className="mb-0" data-testid="candidates-bulk-publish-info">
                    <strong>Publish</strong> walks each candidate through draft → in_review → reviewed → published in one shot. Already-published rows are skipped (not re-logged).
                  </Alert>
                )}
                {bulkConfirm.target === 'archived' && (
                  <Alert variant="warning" className="mb-0">
                    Archived candidates are hidden from the public API. You can restore them later by transitioning back to draft.
                  </Alert>
                )}
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setBulkConfirm(null)} disabled={bulkRunning}>Cancel</Button>
            <Button
              variant={bulkConfirm?.target === 'archived' ? 'warning' : 'success'}
              onClick={runBulk}
              disabled={bulkRunning}
              data-testid="candidates-bulk-confirm-run"
            >
              {bulkRunning ? <><Spinner size="sm" animation="border" /> Running…</> : `Set to ${bulkConfirm?.target}`}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Bulk result summary */}
        <Modal show={!!bulkResult} onHide={() => setBulkResult(null)} centered size="lg" data-testid="candidates-bulk-result-modal">
          <Modal.Header closeButton>
            <Modal.Title>Bulk transition results</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {bulkResult && (
              <>
                <div className="d-flex gap-3 mb-3 flex-wrap">
                  <div className="px-3 py-2 bg-success-subtle text-success rounded">
                    <div className="small">Succeeded</div>
                    <div className="fs-4 fw-semibold" data-testid="bulk-result-succeeded">{bulkResult.succeeded}</div>
                  </div>
                  <div className="px-3 py-2 bg-warning-subtle text-warning rounded">
                    <div className="small">Skipped (already at target)</div>
                    <div className="fs-4 fw-semibold" data-testid="bulk-result-skipped">{bulkResult.skipped}</div>
                  </div>
                  <div className="px-3 py-2 bg-danger-subtle text-danger rounded">
                    <div className="small">Failed</div>
                    <div className="fs-4 fw-semibold" data-testid="bulk-result-failed">{bulkResult.failed}</div>
                  </div>
                </div>
                {(bulkResult.results || []).some((r) => !r.ok && !r.skipped) && (
                  <>
                    <h6 className="mt-3">Failures</h6>
                    <Table size="sm" striped className="mb-0">
                      <thead><tr><th>Candidate</th><th>From</th><th>Reason</th></tr></thead>
                      <tbody>
                        {bulkResult.results.filter((r) => !r.ok && !r.skipped).slice(0, 50).map((r) => (
                          <tr key={r.candidate_id}>
                            <td>{r.full_name || r.candidate_id}</td>
                            <td className="text-muted">{r.from_status || '—'}</td>
                            <td className="small text-danger">{r.reason || 'unknown error'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </>
                )}
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="primary" onClick={() => setBulkResult(null)}>Done</Button>
          </Modal.Footer>
        </Modal>
      </div>
    </>
  );
}
